import * as THREE from "three";
import useMaterial from "@/hooks/useMaterial";
import { useAppStore } from "@/stores/appStore";
import { config_base } from "@/data/config_base";
import { useRef, useMemo } from "react";
import useCopyTexture from "@/hooks/useCopyTexture";
import { rotateUVs } from "@/services/helpers/rotateUV";

const MailboxTop = () => {
	const lastGeometryRef = useRef<THREE.BufferGeometry | null>(null);
	const { pillar } = useAppStore((state) => state.configurator);

	const shape = new THREE.Shape();
	shape.moveTo(0, 0);
	shape.lineTo(0, 0.01);
	shape.lineTo(0.02, 0.005);
	shape.lineTo(0.02, 0);
	shape.closePath();

	const extrudeSettings = {
		depth: 0.1,
		bevelEnabled: true,
		bevelSegments: 5,
		steps: 1,
		bevelSize: 0,
		bevelThickness: 0,
		curveSegments: 12,
	};

	const textures = useCopyTexture(pillar.selectedMaterials[pillar.id]?.textures || [], {
		repeat: pillar.selectedMaterials[pillar.id]?.settings.repeat,
	});

	// Create geometry and dispose of the old one before re-creating
	const geometry = useMemo(() => {
		// Dispose of the previous geometry if it exists
		if (lastGeometryRef.current) {
			lastGeometryRef.current.dispose();
		}

		const newGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

		rotateUVs(newGeometry, {
			front: 90,
			left: 0,
			right: 180,
			back: 270,
			top: 270,
			bottom: 270,
		});

		// Update the reference to the new geometry
		lastGeometryRef.current = newGeometry;
		return newGeometry;
	}, [shape, extrudeSettings]);

	return (
		<mesh
			scale={[1, 1, 1.04]}
			castShadow
			receiveShadow
			position={[0.1 + 0.002, 0.04, -0.002]}
			rotation={[0, -Math.PI / 2, 0]}
			geometry={geometry}>
			<meshStandardMaterial
				{...textures}
				color={pillar.selectedMaterials[pillar.id]?.settings.color}
				roughness={pillar.selectedMaterials[pillar.id]?.settings.roughness}
			/>
		</mesh>
	);
};

const MailboxBox = () => {
	const lastGeometryRef = useRef<THREE.BufferGeometry | null>(null);

	const shape = new THREE.Shape();
	shape.moveTo(0, 0);
	shape.lineTo(0, 0.04);
	shape.lineTo(0.1, 0.04);
	shape.lineTo(0.1, 0);
	shape.closePath();

	const holeShape = new THREE.Path();
	holeShape.moveTo(0.01, 0.01);
	holeShape.lineTo(0.09, 0.01);
	holeShape.quadraticCurveTo(0.1, 0.02, 0.09, 0.03);
	holeShape.lineTo(0.01, 0.03);
	holeShape.quadraticCurveTo(0, 0.02, 0.01, 0.01);
	holeShape.closePath();

	shape.holes.push(holeShape);

	const extrudeSettings = {
		depth: 0.00002,
		bevelEnabled: true,
		bevelSegments: 10,
		steps: 10,
		bevelSize: 0.0015,
		bevelThickness: 0.002,
		curveSegments: 12,
	};

	let material = useMaterial(
		config_base.configurator.materials.find((material) => material.id === "worn_bronze").textures,
		{
			repeat: {
				x: 10,
				y: 10,
			},
			rotation: 0,
		},
		{
			color: "#fff",
			roughness: 0,
		}
	);

	// Create geometry and dispose of the old one before re-creating
	const geometry = useMemo(() => {
		// Dispose of the previous geometry if it exists
		if (lastGeometryRef.current) {
			lastGeometryRef.current.dispose();
		}

		const newGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

		// Update the reference to the new geometry
		lastGeometryRef.current = newGeometry;
		return newGeometry;
	}, [shape, extrudeSettings]);

	return (
		<mesh castShadow receiveShadow position={[0, 0, 0]} rotation={[0, 0, 0]} geometry={geometry} material={material} />
	);
};

const MailboxPlate = () => {
	const lastGeometryRef = useRef<THREE.BufferGeometry | null>(null);
	const shape = new THREE.Shape();

	shape.moveTo(0, 0);
	shape.lineTo(0, 0.04);
	shape.lineTo(0.1, 0.04);
	shape.lineTo(0.1, 0);
	shape.closePath();

	const extrudeSettings = {
		depth: 0,
		bevelEnabled: true,
		bevelSegments: 10,
		steps: 10,
		bevelSize: 0.0015,
		bevelThickness: 0.002,
		curveSegments: 12,
	};

	let material = useMaterial(
		config_base.configurator.materials.find((material) => material.id === "worn_bronze").textures,
		{
			repeat: {
				x: 10,
				y: 10,
			},
			offset: {
				x: 0.05,
				y: 0.05,
			},
			rotation: Math.PI / 2,
		},
		{
			color: "#fff",
			roughness: 0.4,
		}
	);

	// Create geometry and dispose of the old one before re-creating
	const geometry = useMemo(() => {
		// Dispose of the previous geometry if it exists
		if (lastGeometryRef.current) {
			lastGeometryRef.current.dispose();
		}

		const newGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

		// Update the reference to the new geometry
		lastGeometryRef.current = newGeometry;
		return newGeometry;
	}, [shape, extrudeSettings]);

	return (
		<mesh
			castShadow
			receiveShadow
			position={[0, 0, -0.001]}
			rotation={[0, 0, 0]}
			geometry={geometry}
			material={material}
		/>
	);
};

export default function Mailbox({ position }: { position: [number, number, number] }) {
	return (
		<group position={position}>
			<MailboxTop />
			<MailboxBox />
			<MailboxPlate />
		</group>
	);
}
